import { type ReactNode, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Button, Chip, InputAdornment, Paper, Skeleton, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  TablePagination, Typography, alpha, useTheme, Tooltip,
} from '@mui/material';
import { ArrowBack, Search, ManageSearch } from '@mui/icons-material';
import RentalPackagesPanel from '@/components/rental/RentalPackagesPanel';
import { getMasters } from '@/services/platform/masterService';
import { listZones } from '@/services/zoneService';
import { parseMasterListRows } from '@/utils/masterRows';
import { countOutstationPricingGroupsByZone, buildOutstationPricingZoneResolver, lookupOutstationPackageCounts } from '@/utils/outstationPricing';
import { useI18n } from '@/i18n/I18nProvider';
import { translateUiText } from '@/i18n/translateUiText';

function zoneLevelLabel(zoneLevel?: string) {
  return zoneLevel === 'PRIMARY' ? 'Primary' : 'Secondary';
}

interface ZoneMasterPickerProps {
  title: string;
  subtitle: string;
  detailTitle: (zoneName: string, zoneCode: string) => string;
  renderDetail: (zoneCode: string) => React.ReactNode;
  onZoneSelect?: (zoneCode: string) => void;
  onBack?: () => void;
  /** When set, show grouped package counts from the matching master (not raw vehicle rows). */
  packageCountSource?: 'outstation';
}

export function ZoneMasterPicker({
  title,
  subtitle,
  detailTitle,
  renderDetail,
  onZoneSelect,
  onBack,
  packageCountSource,
}: ZoneMasterPickerProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const [zoneCode, setZoneCode] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { data, isLoading } = useQuery({
    queryKey: ['zone-master-picker', search, page, pageSize],
    queryFn: async () => {
      const res = await listZones({ search: search || undefined, page: page + 1, limit: pageSize });
      return {
        data: (res.rows || []).map((z) => ({
          _id: z._id,
          code: z.code,
          name: z.name,
          zoneLevel: z.zoneLevel,
          primaryZoneId: z.primaryZoneId,
          isActive: z.isActive,
        })),
        total: res.meta?.total ?? res.rows?.length ?? 0,
        page: page + 1,
        limit: pageSize,
      };
    },
    enabled: !zoneCode,
  });

  const zones = data?.data || [];
  const total = data?.total || 0;

  const { data: outstationPackageCounts } = useQuery({
    queryKey: ['outstation-pricing-zone-counts'],
    queryFn: async () => {
      const [zonesRes, pricingRes] = await Promise.all([
        listZones({ limit: 500 }),
        getMasters('outstation_pricing', { limit: 500, page: 1 }),
      ]);
      const zoneRows = zonesRes.rows || [];
      const rows = parseMasterListRows<{
        _id: string; code: string; name: string; isActive?: boolean; metadata?: Record<string, unknown>;
      }>(pricingRes);
      const resolvePricingZoneCode = buildOutstationPricingZoneResolver(zoneRows);
      return countOutstationPricingGroupsByZone(rows, resolvePricingZoneCode);
    },
    enabled: packageCountSource === 'outstation' && !zoneCode,
  });

  const { data: allZonesMeta = [] } = useQuery({
    queryKey: ['zones-outstation-meta'],
    queryFn: async () => {
      const res = await listZones({ limit: 500 });
      return res.rows || [];
    },
    enabled: packageCountSource === 'outstation' && !zoneCode,
  });

  const primaryZoneById = useMemo(
    () => new Map(allZonesMeta.map((z) => [z._id, z])),
    [allZonesMeta],
  );

  const resolveOutstationCounts = useMemo(
    () => buildOutstationPricingZoneResolver(allZonesMeta),
    [allZonesMeta],
  );

  const { data: zoneInfo } = useQuery({
    queryKey: ['zone-by-code', zoneCode],
    queryFn: async () => {
      const res = await listZones({ limit: 200 });
      return res.rows?.find((z) => z.code === zoneCode);
    },
    enabled: !!zoneCode,
  });

  if (zoneCode) {
    const zoneTitle = zoneInfo?.name || zoneCode;
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => {
            if (onBack) onBack();
            else setZoneCode(null);
          }} size="small">
            All Zones
          </Button>
        </Stack>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {translateUiText(detailTitle(zoneTitle, zoneCode), t)}
        </Typography>
        {renderDetail(zoneCode)}
      </Box>
    );
  }

  return (
    <Box>
      {title ? (
        <>
          <Typography variant="h6" fontWeight={700} gutterBottom>{translateUiText(title, t)}</Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{translateUiText(subtitle, t)}</Typography>
          ) : null}
        </>
      ) : null}

      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2.5, border: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          placeholder="Search zones…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280 }}
        />
      </Paper>

      {isLoading ? (
        <Stack spacing={1}>{[1, 2, 3, 4].map((i) => <Skeleton key={i} height={48} />)}</Stack>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 56 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Zone</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Level</TableCell>
                  {packageCountSource === 'outstation' && (
                    <TableCell sx={{ fontWeight: 700 }}>Packages</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 140 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {zones.map((zone, index) => {
                  const isSecondary = zone.zoneLevel === 'SECONDARY';
                  const primaryZone = zone.primaryZoneId ? primaryZoneById.get(zone.primaryZoneId) : undefined;
                  const packageCounts = outstationPackageCounts
                    ? lookupOutstationPackageCounts(zone.code, outstationPackageCounts, resolveOutstationCounts)
                    : undefined;
                  const manageDisabled = packageCountSource === 'outstation' && isSecondary;
                  const manageTooltip = manageDisabled && primaryZone
                    ? `Outstation pricing is configured on the primary zone only (${primaryZone.name})`
                    : manageDisabled
                      ? 'Outstation pricing is configured on primary zones only'
                      : '';
                  return (
                  <TableRow
                    key={zone._id}
                    hover
                    sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
                  >
                    <TableCell>{page * pageSize + index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{zone.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{zone.code}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={zoneLevelLabel(zone.zoneLevel)}
                        color={zone.zoneLevel === 'PRIMARY' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    {packageCountSource === 'outstation' && (
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={
                            isSecondary
                              ? `${packageCounts?.total ?? 0} total · ${packageCounts?.active ?? 0} active (primary)`
                              : `${packageCounts?.total ?? 0} total · ${packageCounts?.active ?? 0} active`
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        size="small"
                        label={zone.isActive ? 'Active' : 'Inactive'}
                        color={zone.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {manageDisabled ? (
                        <Tooltip title={manageTooltip}>
                          <span>
                            <Button size="small" variant="contained" disabled startIcon={<ManageSearch fontSize="small" />}>
                              Manage
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<ManageSearch fontSize="small" />}
                          onClick={() => {
                            if (onZoneSelect) onZoneSelect(zone.code);
                            else setZoneCode(zone.code);
                          }}
                        >
                          Manage
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
                {!zones.length && (
                  <TableRow>
                    <TableCell colSpan={packageCountSource === 'outstation' ? 6 : 5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No zones found. Create zones under Zone Management first.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}
    </Box>
  );
}

export default function ServiceRentalMasterTab() {
  return (
    <ZoneMasterPicker
      title="Rental Management"
      subtitle="Select a zone to manage hour/km rental packages — same flow as Caby rental master."
      detailTitle={(name, code) => `Rental Packages — ${name} (${code})`}
      renderDetail={(zoneCode) => (
        <RentalPackagesPanel
          lockedZoneCode={zoneCode}
          unifiedTransportRental
          subtitle="Use Add Package to create hour/km bundles for all transport rental services. Edit or delete existing packages below."
        />
      )}
    />
  );
}
